import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import styles from "../../styles/components/UI/SearchableDropdown.module.css"
import { ErrorPopup } from '../error/ErrorPopup';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { LangContext } from "../../context/LangContext";
import { ThemeContext } from "../../context/ThemeContext";

const translations = {
  ru: {
    placeholder: "Выберите...",
    noResults: "Нет результатов",
  },
  en: {
    placeholder: "Select...",
    noResults: "No results",
  },
};


export const SearchableDropdown = ({
  options,
  selectedValue,
  onSelect,
  label,
  placeholder,
  hasError,
  errorMessage,
  onFocus,
  onBlur,
  isTouched,
}) => {
  const { langSelected } = useContext(LangContext);
  const { theme } = useContext(ThemeContext);
  const t = translations[langSelected] || translations.ru;
  const isDark = theme === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [isMouseEnter, setIsMouseEnter] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (selectedValue) {
      const selectedOption = options.find(option => option.value === selectedValue);
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      } else {
        setSearchTerm('');
      }
    } else {
      setSearchTerm('');
    }
  }, [selectedValue, options]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    const lowercasedValue = value.toLowerCase();

    const newFilteredOptions = options.filter(option =>
      option.label.toLowerCase().includes(lowercasedValue)
    );
    setFilteredOptions(newFilteredOptions);
    setIsOpen(true);

    const matchedOption = options.find(option => option.label.toLowerCase() === lowercasedValue);
    if (!matchedOption) {
      onSelect(null);
    }

    if (onFocus) onFocus(label);
  };

  const handleOptionClick = (option) => {
    setSearchTerm(option.label);
    onSelect(option.value);
    setIsOpen(false);
    if (onBlur) onBlur(label);
  };

  const handleToggleDropdown = () => {
      setIsOpen((prev) => !prev);
      if (!isOpen && onFocus) onFocus(label);
      if (isOpen && onBlur) onBlur(label);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    onSelect(null);
    setIsOpen(false);
    if (onBlur) onBlur(label);
  };

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      if (selectedValue) {
        const selectedOption = options.find(option => option.value === selectedValue);
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        } else {
          setSearchTerm('');
          onSelect(null);
        }
      } else {
        setSearchTerm('');
      }
      if (onBlur) onBlur(label);
    }
  }, [selectedValue, options, onSelect, onBlur, label]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleMouseEnter = () => {
    setIsMouseEnter(true);
  };

  const handleMouseLeave = () => {
    setIsMouseEnter(false);
  };

  return (
    <div
      className={`${styles.searchableDropdown} ${hasError && isTouched ? styles.errorContainer : ''} ${isDark ? styles.darkSearchableDropdown : ''}`}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {label && <label className={isDark ? styles.darkLabel : ''}>{label}:</label>}
      <div className={styles.inputContainer}>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            if (onFocus) onFocus(label);
          }}
          placeholder={placeholder || t.placeholder}
          className={`${styles.searchInput} ${hasError && isTouched ? styles.errorInput : ''} ${isDark ? styles.darkSearchInput : ''}`}
        />
        <div className={styles.iconWrapper}>
            {selectedValue && (
                <div className={`${styles.clearIcon} ${isDark ? styles.darkClearIcon : ''}`} onClick={handleClear}>
                    <CloseIcon fontSize="inherit" />
                </div>
            )}
            <div className={`${styles.dropdownToggle} ${isDark ? styles.darkDropdownToggle : ''}`} onClick={handleToggleDropdown}>
                <KeyboardArrowDownIcon fontSize="inherit" />
            </div>
        </div>
      </div>

      {isOpen && (
        <ul className={`${styles.optionsList} ${isDark ? styles.darkOptionsList : ''}`}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.value || index}
                onClick={() => handleOptionClick(option)}
                className={`${styles.optionItem} ${option.value === selectedValue ? styles.selected : ""} ${isDark ? styles.darkOptionItem : ''} ${option.value === selectedValue && isDark ? styles.darkSelected : ''}`}
              >
                <div className={`${styles.optionLabel} ${isDark ? styles.darkOptionLabel : ''}`}>{option.label}</div>
              </li>
            ))
          ) : (
            <li className={`${styles.noResults} ${isDark ? styles.darkNoResults : ''}`}>{t.noResults}</li>
          )}
        </ul>
      )}
      {hasError && isTouched && isMouseEnter && errorMessage && <ErrorPopup message={errorMessage} theme={theme} />}
    </div>
  );
};
